// AuditLogs.jsx
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import { Button, Spinner, Form, Modal,Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Container from '../../layouts/Container';
import PaginatedListTable from '../../components/PaginatedListTable';
import { formatGMT8DateTime, getToday } from '../../utils/dateUtil';
import { getProjectAuditLogs } from '../../services/auditService';

function ViewAuditLogModal({ show, log, onClose }) {
  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Audit Log Detail</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {log ? (
          <>
            <div className="mb-2"><strong>When:</strong> {formatGMT8DateTime(log.createdAt)}</div>
            <div className="mb-2"><strong>User:</strong> {log.username}</div>
            <div className="mb-2"><strong>Subject:</strong> {log.subjectName}</div>
            <div className="mb-2"><strong>Subject ID:</strong> {log.subjectId}</div>
            <div className="mb-2"><strong>Action:</strong> {log.action}</div>
            <div className="mb-2"><strong>Project ID:</strong> {log.projectId ?? '—'}</div>
            <hr />
            <div className="mb-2"><strong>Changes (diff):</strong></div>
            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
{JSON.stringify(log.updatedValue, null, 2)}
            </pre>
          </>
        ) : '—'}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

const buildColumns = (onView) => {
  const columnHelper = createColumnHelper();
  return [
    columnHelper.display({
      id: 'createdAt',
      header: 'Date',
      cell: info => {
        const row = info.row.original.raw;
        return row?.createdAt ? formatGMT8DateTime(row.createdAt) : '—';
      },
    }),
    columnHelper.accessor('username', { header: 'Username' }),
    columnHelper.accessor('subjectName', { header: 'Subject' }),
    columnHelper.accessor('subjectId', { header: 'Subject ID' }),
    columnHelper.accessor('action', { header: 'Action' }),
    columnHelper.display({
      id: 'changes',
      header: 'Changed Fields',
      cell: info => {
        const diff = info.row.original.raw?.updatedValue || {};
        const keys = diff && typeof diff === 'object' ? Object.keys(diff) : [];
        if (!keys.length) return '—';
        return `${keys.length} field${keys.length > 1 ? 's' : ''}: ${keys.join(', ')}`;
      },
    }),
  ];
};

function AuditTable({ title, subjects, projectId, startDateISO, endDateISO }) {
  const tableRef = useRef();
  const [showModal, setShowModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const handleApplyLocal = () => tableRef.current?.refetch({ page: 0 });

  const onView = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const columns = useMemo(() => buildColumns(onView), []);

  const fetchPaginatedLogs = async ({ pageIndex, pageSize, search }) => {
    try {
      const res = await getProjectAuditLogs(projectId, {
        page: pageIndex,
        size: pageSize,
        subjects: subjects.join(','),   // CSV as per your endpoint
        search, // if you add search server-side later
      });

      const rows = (res.data || []).map(log => ({
        id: log.id,
        username: log.username || '-',
        subjectName: log.subjectName || '-',
        subjectId: log.subjectId || '-',
        action: log.action || '-',
        raw: log,
      }));

      const pageCount = res.pageCount ?? res.totalPages ?? Math.ceil((res.pagination?.total || 0) / (res.pagination?.pageSize || pageSize)) ?? 0;
      const totalItems = res.totalItems ?? res.pagination?.total ?? 0;

      return { data: rows, pageCount, totalItems };
    } catch (e) {
      console.error(e);
      toast.error(`Error fetching ${title} logs.`);
      return { data: [], pageCount: 0, totalItems: 0 };
    }
  };

  return (
    <div className="mb-5">
      {/* <div className="d-flex align-items-center justify-content-between mb-2">
        <h4 className="mb-0">{title}</h4>
        <Button variant="secondary" size="sm" onClick={handleApplyLocal}>Refresh</Button>
      </div> */}

      <PaginatedListTable
        ref={tableRef}
        tableId={`audit-${title.toLowerCase().replace(/\s+/g, '-')}`}
        columns={columns}
        onFetchData={fetchPaginatedLogs}
        onRowClick={(row) => onView(row.raw)}
      />

      <ViewAuditLogModal show={showModal} log={selectedLog} onClose={() => setShowModal(false)} />
    </div>
  );
}

export default function AuditLogs() {
  const { projectId } = useParams();

  // date-range (same UX as Activity)
  const [startDate, setStartDate] = useState(getToday());
  const [endDate, setEndDate] = useState(getToday());
  const startDateISO = useMemo(() => new Date(`${startDate}T00:00:00+08:00`).toISOString(), [startDate]);
  const endDateISO = useMemo(() => new Date(`${endDate}T23:59:59+08:00`).toISOString(), [endDate]);

  // Refetch hinting across tables when "Apply" is clicked
  const userRef = useRef();
  const roleRef = useRef();
  const formsRef = useRef();

  // wire child refs after mount (optional)
  useEffect(() => {
    userRef.current = document.getElementById('audit-user');
    roleRef.current = document.getElementById('audit-role');
    formsRef.current = document.getElementById('audit-forms');
  }, []);

  const handleApplyAll = () => {
    // Ask each table to reset & refetch
    const buttons = Array.from(document.querySelectorAll('button')).filter(b => b.textContent === 'Refresh');
    buttons.forEach(b => b.click());
  };

  return (
    <Container>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h2 className="mb-0">Audit Logs</h2>

        {/* Date-range controls
        <Form className="d-flex align-items-center gap-3 flex-wrap mb-0">
          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">Start</Form.Label>
            <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </Form.Group>

          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">End</Form.Label>
            <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </Form.Group>

          <Button variant="primary" type="button" onClick={handleApplyAll}>
            Apply
          </Button>
        </Form> */}
      </div>

      {/* Three tables with different subjects */}

      
      
      
        <Tabs defaultActiveKey="active" id="user-tabs" className="mb-3">
          {/* Active Users Tab */}
          <Tab eventKey="active" title="Users">
            
      <AuditTable
        title="User"
        subjects={['User']}
        projectId={projectId}
        startDateISO={startDateISO}
        endDateISO={endDateISO}
      /></Tab>

          {/* Inactive Users Tab */}
          <Tab eventKey="inactive" title="Roles">
            <AuditTable
        title="Role"
        subjects={['Role']}
        projectId={projectId}
        startDateISO={startDateISO}
        endDateISO={endDateISO}
      />
</Tab>
          {/* Inactive Users Tab */}
          <Tab eventKey="engagement" title="Engagement">
            <AuditTable
        title="Engagement"
        subjects={['Engagement']}
        projectId={projectId}
        startDateISO={startDateISO}
        endDateISO={endDateISO}
      />
</Tab>

          {/* Disabled Users Tab */}
          <Tab eventKey="disabled" title="Forms">
            <AuditTable
        title="Forms (Form, FormGroup, Field)"
        subjects={['Form', 'FormGroup', 'Field']}
        projectId={projectId}
        startDateISO={startDateISO}
        endDateISO={endDateISO}
      />
</Tab>
        </Tabs>
    </Container>
  );
}
